import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

const CELL_EMPTY = 0;
const CELL_MARKER = 1;
const CELL_BLOCKED = 2;

function getCellKey(row, col) {
  return `${row}-${col}`;
}

function regionColor(regionId) {
  const palette = [
    'bg-red-200 dark:bg-red-200',
    'bg-blue-200 dark:bg-blue-200',
    'bg-yellow-200 dark:bg-yellow-200',
    'bg-green-200 dark:bg-green-200',
    'bg-pink-200 dark:bg-pink-200',
    'bg-indigo-200 dark:bg-indigo-200',
    'bg-orange-200 dark:bg-orange-200',
    'bg-cyan-200 dark:bg-cyan-200',
    'bg-lime-200 dark:bg-lime-200',
    'bg-fuchsia-200 dark:bg-fuchsia-200',
  ];
  return palette[Math.abs(Number(regionId || 0)) % palette.length];
}

function TerritoryGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const [mode, setMode] = useState('marker');
  const [selectedCell, setSelectedCell] = useState(null);
  const [hintBusy, setHintBusy] = useState(false);
  const holdTimeoutRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const suppressNextContextMenuRef = useRef(false);

  const meId = user?.socketId || user?.userId || null;
  const puzzle = gameState?.puzzle || {};
  const size = Number(puzzle.size || 0);
  const regions = Array.isArray(puzzle.regions) ? puzzle.regions : [];
  const solutionCols = Array.isArray(puzzle.solutionCols) ? puzzle.solutionCols : [];
  const finished = !!gameState?.finished;
  const playerProgress = useMemo(() => gameState?.playerProgress || {}, [gameState?.playerProgress]);
  const myProgress = meId ? playerProgress[meId] : null;

  const board = myProgress?.board || [];
  const conflicts = useMemo(() => new Set(Array.isArray(myProgress?.conflicts) ? myProgress.conflicts : []), [myProgress?.conflicts]);
  const lastHint = myProgress?.lastHint || null;
  const validateConflicts = gameState?.validateConflicts !== false;
  const allowRevealHint = gameState?.allowRevealHint !== false;

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  useEffect(() => {
    if (lastHint?.cell && Number.isInteger(lastHint.cell.row) && Number.isInteger(lastHint.cell.col)) {
      setSelectedCell({ row: lastHint.cell.row, col: lastHint.cell.col });
    }
  }, [lastHint]);

  const clearHoldTimeout = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  };

  const sendCellAction = async (row, col, desiredMode) => {
    if (finished) return;
    await dispatchAction({ type: 'set-cell', row, col, mode: desiredMode });
  };

  const onCellClick = async (row, col) => {
    setSelectedCell({ row, col });
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    await sendCellAction(row, col, mode === 'blocked' ? 'blocked' : 'marker');
  };

  const onCellContextMenu = async (event, row, col) => {
    event.preventDefault();
    if (suppressNextContextMenuRef.current) {
      suppressNextContextMenuRef.current = false;
      return;
    }
    setSelectedCell({ row, col });
    await sendCellAction(row, col, 'blocked');
  };

  const onTouchStart = (row, col) => {
    setSelectedCell({ row, col });
    suppressNextClickRef.current = false;
    clearHoldTimeout();
    holdTimeoutRef.current = setTimeout(async () => {
      suppressNextClickRef.current = true;
      suppressNextContextMenuRef.current = true;
      await sendCellAction(row, col, 'blocked');
    }, 420);
  };

  const onTouchEnd = () => {
    clearHoldTimeout();
  };

  const requestHint = async (hintType) => {
    if (hintBusy || finished) return;
    setHintBusy(true);
    try {
      const action = { type: 'request-hint', hintType };
      if (hintType === 'explain-cell' && selectedCell) {
        action.row = selectedCell.row;
        action.col = selectedCell.col;
      }
      await dispatchAction(action);
    } finally {
      setHintBusy(false);
    }
  };

  const clearBoard = async () => {
    if (finished) return;
    await dispatchAction({ type: 'clear-board' });
  };

  const progressRows = useMemo(() => {
    const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;
    return players.map((player) => {
      const playerId = player.socketId || player.userId;
      const progress = playerProgress[playerId] || {};
      return {
        id: playerId,
        username: player.username || 'Player',
        rowsSolved: Number(progress.rowsSolved || 0),
        colsSolved: Number(progress.colsSolved || 0),
        regionsSolved: Number(progress.regionsSolved || 0),
        markerCount: Number(progress.markerCount || 0),
        completed: !!progress.completed,
        isMe: playerId === meId,
      };
    });
  }, [gamePlayers, gameState?.players, meId, playerProgress]);

  const renderCell = (row, col) => {
    const value = Number(board?.[row]?.[col] ?? CELL_EMPTY);
    const isConflict = conflicts.has(getCellKey(row, col));
    const regionId = Number(regions?.[row]?.[col] ?? 0);
    const isSelected = selectedCell?.row === row && selectedCell?.col === col;

    return (
      <button
        key={getCellKey(row, col)}
        type="button"
        className={[
          'relative flex aspect-square items-center justify-center overflow-hidden border border-slate-500 font-bold leading-none transition select-none',
          regionColor(regionId),
          isSelected ? 'ring-2 ring-amber-500 ring-inset' : '',
          isConflict && validateConflicts ? 'ring-2 ring-red-500 ring-inset' : '',
          value === CELL_MARKER || value === CELL_BLOCKED ? 'text-black' : 'text-slate-500 dark:text-slate-300',
        ].join(' ')}
        onClick={() => onCellClick(row, col)}
        onContextMenu={(event) => onCellContextMenu(event, row, col)}
        onTouchStart={() => onTouchStart(row, col)}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {value === CELL_MARKER ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[2rem] leading-none text-black">●</span>
        ) : null}
        {value === CELL_BLOCKED ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[2rem] leading-none text-black">×</span>
        ) : null}
      </button>
    );
  };

  const renderSolutionCell = (row, col) => {
    const regionId = Number(regions?.[row]?.[col] ?? 0);
    const hasMarker = Number(solutionCols?.[row]) === col;
    return (
      <div
        key={`solution-${row}-${col}`}
        className={[
          'relative flex aspect-square items-center justify-center overflow-hidden border border-slate-500 font-bold leading-none',
          regionColor(regionId),
          'text-black',
        ].join(' ')}
      >
        {hasMarker ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[2rem] leading-none text-black">●</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white p-4 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">

      {finished && solutionCols.length === size && size > 0 ? (
        <div className="mb-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Solution</div>
          <div
            className="mt-3 grid overflow-hidden border border-slate-500 bg-slate-100 dark:bg-slate-900"
            style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: size }, (_, row) => Array.from({ length: size }, (_, col) => renderSolutionCell(row, col)))}
          </div>
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Correct marker placement for this puzzle.
          </div>
        </div>
      ) : null}


      <div className="mb-4 space-y-4">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Board</div>
            {!finished ? (
              <div className="flex items-center gap-2 rounded-full bg-slate-200 p-1 dark:bg-slate-700">
                <button
                  type="button"
                  onClick={() => setMode('marker')}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${mode === 'marker' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
                >
                  Marker Mode
                </button>
                <button
                  type="button"
                  onClick={() => setMode('blocked')}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${mode === 'blocked' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
                >
                  Block Mode
                </button>
              </div>
            ) : null}
          </div>

          <div
            className="grid overflow-hidden border border-slate-500 bg-slate-100 dark:bg-slate-900"
            style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: size }, (_, row) => Array.from({ length: size }, (_, col) => renderCell(row, col)))}
          </div>

          <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Desktop: left click places/removes marker, right click toggles blocked. Mobile: tap uses mode, long press blocks.
          </div>
        </div>

        {!finished ? (
          <div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Actions</div>
              {allowRevealHint ? (
                <>
                  <div className="mt-3">
                    <button type="button" onClick={() => requestHint('reveal-marker')} disabled={hintBusy || finished} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-700">Reveal Marker</button>
                  </div>
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Use Reveal Marker only when stuck. It can reduce your score.
                  </div>
                </>
              ) : null}
              <button type="button" onClick={clearBoard} disabled={finished} className="mt-3 w-full rounded-xl bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-700 dark:hover:bg-slate-600">Clear Board</button>

              {lastHint ? (
                <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
                  <div className="font-semibold">{lastHint.message}</div>
                  {Number(myProgress?.lastHintPenalty || 0) > 0 ? (
                    <div className="mt-1 font-semibold text-rose-700 dark:text-rose-300">
                      Score penalty: -{Number(myProgress?.lastHintPenalty || 0)}
                    </div>
                  ) : null}
                  {!validateConflicts ? (
                    <div className="mt-1 text-slate-600 dark:text-slate-300">Conflict highlighting is disabled in this room.</div>
                  ) : null}
                  {Array.isArray(lastHint.reasons) && lastHint.reasons.length > 0 ? (
                    <ul className="mt-1 list-disc pl-4">
                      {lastHint.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Room Progress</div>
        <div className="mt-3 space-y-2">
          {progressRows.map((entry) => (
            <div key={entry.id} className={`flex flex-wrap items-center justify-between rounded-2xl px-3 py-2 text-sm ${entry.isMe ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-white dark:bg-slate-900'}`}>
              <div className="font-semibold">{entry.username}{entry.isMe ? ' (You)' : ''}</div>
              <div className="text-slate-600 dark:text-slate-300">
                {entry.rowsSolved}/{size} rows, {entry.colsSolved}/{size} cols, {entry.regionsSolved}/{size} regions
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

TerritoryGame.Guide = function TerritoryGuide() {
  return (
    <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
      <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Territory - Detailed Guide</h3>

      <p>
        Goal: place exactly one marker in every row, every column, and every colored territory.
        Markers cannot touch, including diagonally.
      </p>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mb-1 font-semibold">Legend</div>
        <div>O = marker, X = blocked note, . = empty cell</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="font-semibold">Step 1: Place obvious singles first</div>
        <p className="mt-1">
          Scan rows and territories. If only one cell can legally hold a marker, place it.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="font-semibold">Step 2: Block impossible cells</div>
        <p className="mt-1">
          When a marker is placed, block cells that now violate row, column, territory, or touching rules.
        </p>
        <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 px-3 py-2 text-xs leading-5 text-slate-100">
{`Example (after placing O):

. . . .
. O X .
. X X .
. . . .

The X lls are blocked because they touch O.`}
        </pre>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="font-semibold">Step 3: Keep counts balanced</div>
        <p className="mt-1">
          Use the progress panel: each row/column/territory must end at exactly 1 marker.
          If any reaches 2, backtrack immediately.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="font-semibold">Step 4: Use hints efficiently</div>
        <div className="mt-1 space-y-1">
          <div>Reveal Marker: use sparingly if fully stuck but this will reduce your score.</div>
        </div>
      </div>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
        <div className="font-semibold">Quick Win Checklist</div>
        <div className="mt-1">1) Every row has one O</div>
        <div>2) Every column has one O</div>
        <div>3) Every territory has one O</div>
        <div>4) No O touches another O, even diagonally</div>
      </div>
    </div>
  );
};

export default TerritoryGame;
